/**
 * KoolKonnectProjectPage — Phase 4-7 UI surface for a single project.
 * Opens when a project card is clicked. Houses:
 *   • Plan tab           (Phase 6 — Autonomous planner output)
 *   • Storyboard/Scenes  (Phase 5 — scene-by-scene movie flow)
 *   • Outputs            (all generated assets + consistency scoring)
 *   • Assembly           (Phase 7 — timeline manifest editor)
 *   • Backup             (Phase 8 — Drive backup + retention)
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Wand2, Loader2, Film, Play, Image as ImageIcon, Sparkles, Music,
  ScrollText, ShieldCheck, CloudUpload, ChevronRight, Layers, Plus, RefreshCw,
  Trash2, Save,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import GenerationProgress from '../components/koolkonnect/GenerationProgress';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function KoolKonnectProjectPage() {
  const { pid } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState('plan');
  const [project, setProject] = useState(null);
  const [brand, setBrand] = useState(null);
  const [scenes, setScenes] = useState([]);
  const [outputs, setOutputs] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const { data: p } = await axios.get(`${API}/api/koolkonnect/projects/${pid}`, auth());
      setProject(p);
      if (p.brand_id) {
        try {
          const { data: b } = await axios.get(`${API}/api/koolkonnect/brands/${p.brand_id}`, auth());
          setBrand(b);
        } catch (err) { /* brand may be deleted */ }
      }
      const [{ data: sc }, { data: lib }] = await Promise.all([
        axios.get(`${API}/api/koolkonnect/projects/${pid}/scenes`, auth()),
        axios.get(`${API}/api/koolkonnect/library`, { ...auth(), params: { project_id: pid, limit: 200 } }),
      ]);
      setScenes(sc || []);
      setOutputs(lib || []);
    } catch (e) {
      toast.error('Failed to load project');
    }
  };
  useEffect(() => { load(); }, [pid]);

  const runPlan = async () => {
    setBusy('plan');
    try {
      await axios.post(`${API}/api/koolkonnect/projects/${pid}/plan`, {}, auth());
      toast.success('Production plan generated via Kaharian Gateway');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Plan failed'); }
    finally { setBusy(null); }
  };

  const runStoryboard = async () => {
    setBusy('storyboard');
    try {
      const { data } = await axios.post(`${API}/api/koolkonnect/projects/${pid}/storyboard`, {}, auth());
      toast.success(`Storyboard generated — ${data.generated} scenes`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Storyboard failed'); }
    finally { setBusy(null); }
  };

  if (!project) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="kk-project-page">
      {/* Hero */}
      <div className="bg-gradient-to-br from-indigo-950 via-purple-900 to-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button onClick={() => navigate('/koolkonnect')} className="text-indigo-200 text-xs flex items-center gap-1 hover:text-white mb-3" data-testid="back-btn">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to KoolKonnect
          </button>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <Badge className="bg-white/10 backdrop-blur border-0 text-xs mb-2">{project.type}</Badge>
              <h1 className="text-2xl sm:text-3xl font-bold">{project.name}</h1>
              {brand && <p className="text-sm text-indigo-100 mt-1">Brand · <strong>{brand.name}</strong></p>}
              {project.brief && <p className="text-sm text-indigo-100/80 mt-2 max-w-2xl line-clamp-2">{project.brief}</p>}
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-white/10 backdrop-blur border-0">{project.status}</Badge>
              <Badge className="bg-white/10 backdrop-blur border-0">{outputs.length} outputs</Badge>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-5 mb-6 h-auto bg-white shadow-sm w-full">
            <TabsTrigger value="plan" className="gap-1.5 py-2 data-[state=active]:bg-indigo-950 data-[state=active]:text-white" data-testid="tab-plan"><Wand2 className="h-4 w-4" /><span className="hidden sm:inline">Plan</span></TabsTrigger>
            <TabsTrigger value="scenes" className="gap-1.5 py-2 data-[state=active]:bg-indigo-950 data-[state=active]:text-white" data-testid="tab-scenes"><Film className="h-4 w-4" /><span className="hidden sm:inline">Scenes</span></TabsTrigger>
            <TabsTrigger value="outputs" className="gap-1.5 py-2 data-[state=active]:bg-indigo-950 data-[state=active]:text-white" data-testid="tab-outputs"><ImageIcon className="h-4 w-4" /><span className="hidden sm:inline">Outputs</span></TabsTrigger>
            <TabsTrigger value="assembly" className="gap-1.5 py-2 data-[state=active]:bg-indigo-950 data-[state=active]:text-white" data-testid="tab-assembly"><Layers className="h-4 w-4" /><span className="hidden sm:inline">Assembly</span></TabsTrigger>
            <TabsTrigger value="backup" className="gap-1.5 py-2 data-[state=active]:bg-indigo-950 data-[state=active]:text-white" data-testid="tab-backup"><CloudUpload className="h-4 w-4" /><span className="hidden sm:inline">Backup</span></TabsTrigger>
          </TabsList>

          <TabsContent value="plan"><PlanTab project={project} busy={busy} onRun={runPlan} /></TabsContent>
          <TabsContent value="scenes"><ScenesTab pid={pid} scenes={scenes} onReload={load} onGenStoryboard={runStoryboard} busy={busy} /></TabsContent>
          <TabsContent value="outputs"><OutputsTab outputs={outputs} onReload={load} project={project} brand={brand} /></TabsContent>
          <TabsContent value="assembly"><AssemblyTab pid={pid} scenes={scenes} outputs={outputs} /></TabsContent>
          <TabsContent value="backup"><BackupTab /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ═══ Plan tab ════════════════════════════════════════════════════════════════
function PlanTab({ project, busy, onRun }) {
  const plan = project.plan;
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-600">The Planning Agent turns your brief into a full production plan via Kaharian Gateway.</p>
        <Button onClick={onRun} disabled={busy === 'plan'} className="bg-indigo-600 hover:bg-indigo-700" data-testid="run-plan-btn">
          {busy === 'plan' ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Planning…</> : <><Wand2 className="h-4 w-4 mr-1" /> {plan ? 'Regenerate Plan' : 'Generate Plan'}</>}
        </Button>
      </div>
      {!plan ? (
        <Card className="border-dashed border-2"><CardContent className="py-10 text-center text-sm text-slate-500">No plan yet. Click <strong>Generate Plan</strong> to have the AI Creative Director analyze your brief and produce a concept, deliverables list, scene outline, and asset requirements.</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {plan.concept && (
            <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Concept</CardTitle></CardHeader><CardContent className="text-sm text-slate-700">{plan.concept}</CardContent></Card>
          )}
          {plan.assembly_notes && (
            <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Assembly Notes</CardTitle></CardHeader><CardContent className="text-sm text-slate-700 whitespace-pre-wrap">{plan.assembly_notes}</CardContent></Card>
          )}
          {plan.deliverables && (
            <Card className="md:col-span-2"><CardHeader className="pb-2"><CardTitle className="text-sm">Deliverables ({plan.deliverables.length})</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {plan.deliverables.map((d, i) => (
                    <div key={i} className="p-2 border rounded text-xs">
                      <div className="font-medium">{d.name}</div>
                      <div className="text-slate-500 mt-0.5">{[d.format, d.aspect_ratio, d.platform].filter(Boolean).join(' · ')}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          {plan.required_references && (
            <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Required References</CardTitle></CardHeader>
              <CardContent className="text-xs space-y-1">
                {(plan.required_references || []).map((r, i) => <div key={i} className="flex items-center gap-1"><ChevronRight className="h-3 w-3" /> {r}</div>)}
              </CardContent>
            </Card>
          )}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Audio</CardTitle></CardHeader>
            <CardContent className="text-xs space-y-1">
              <div>Voice: <strong>{plan.voice_needed?.bool || plan.voice_needed === true ? 'Yes' : 'No'}</strong></div>
              {plan.voice_needed?.notes && <div className="text-slate-500">{plan.voice_needed.notes}</div>}
              <div className="mt-2">Music: <strong>{plan.music_needed?.bool || plan.music_needed === true ? 'Yes' : 'No'}</strong></div>
              {plan.music_needed?.style_notes && <div className="text-slate-500">{plan.music_needed.style_notes}</div>}
            </CardContent>
          </Card>
          {plan.estimated_credits !== undefined && (
            <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Est. credits</CardTitle></CardHeader><CardContent className="text-2xl font-bold text-indigo-600">{plan.estimated_credits}</CardContent></Card>
          )}
        </div>
      )}
    </div>
  );
}

// ═══ Scenes tab ══════════════════════════════════════════════════════════════
function ScenesTab({ pid, scenes, onReload, onGenStoryboard, busy }) {
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', prompt: '', shot_type: 'medium', duration_sec: 8 });
  // Map<sceneId, { kind, startedAt }> — tracks live generation per scene so
  // GenerationProgress can render a smooth % bar in the row.
  const [genState, setGenState] = useState({});
  const genScene = async (sceneId, kind) => {
    try {
      const { data: job } = await axios.post(`${API}/api/koolkonnect/scenes/${sceneId}/generate`, { kind }, auth());
      setGenState(s => ({ ...s, [sceneId]: { kind, startedAt: Date.now() } }));
      toast.success(kind === 'video' ? 'Video generation queued' : 'Image generation queued');
      // Kaharian Hub v10.3.5 required — poll every 5s, timeout 360s (72 tries).
      // Video generation typically finishes in 90–180s; complex renders can take up to 300s.
      let tries = 0;
      const timer = setInterval(async () => {
        tries += 1;
        if (tries > 72) { clearInterval(timer); setGenState(s => { const c = { ...s }; delete c[sceneId]; return c; }); toast.error('Generation timed out after 6 min — credits refunded.'); return; }
        try {
          const { data: jd } = await axios.get(`${API}/api/koolkonnect/jobs/${job.id}`, auth());
          if (jd.status === 'complete') {
            clearInterval(timer);
            setGenState(s => { const c = { ...s }; delete c[sceneId]; return c; });
            toast.success(`${kind === 'video' ? 'Video' : 'Image'} ready`);
            onReload();
          } else if (jd.status === 'failed') {
            clearInterval(timer);
            setGenState(s => { const c = { ...s }; delete c[sceneId]; return c; });
            toast.error(`Generation failed: ${jd.error || 'unknown'}`);
          }
        } catch { /* keep polling */ }
      }, 5000);
    } catch (e) {
      setGenState(s => { const c = { ...s }; delete c[sceneId]; return c; });
      toast.error(e?.response?.data?.detail || 'Generation failed');
    }
  };
  const createScene = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    await axios.post(`${API}/api/koolkonnect/projects/${pid}/scenes`, form, auth());
    toast.success('Scene added');
    setCreating(false); setForm({ title: '', description: '', prompt: '', shot_type: 'medium', duration_sec: 8 });
    onReload();
  };
  const removeScene = async (sid) => {
    if (!window.confirm('Delete this scene?')) return;
    await axios.delete(`${API}/api/koolkonnect/scenes/${sid}`, auth());
    onReload();
  };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="outline" onClick={onGenStoryboard} disabled={busy === 'storyboard'} data-testid="gen-storyboard-btn">
          {busy === 'storyboard' ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Storyboarding…</> : <><Sparkles className="h-4 w-4 mr-1" /> Auto-Storyboard</>}
        </Button>
        <Button onClick={() => setCreating(!creating)} data-testid="add-scene-btn"><Plus className="h-4 w-4 mr-1" /> Add Scene</Button>
      </div>
      {creating && (
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="scene-title" /></div>
              <div><Label>Duration (sec)</Label><Input type="number" value={form.duration_sec} onChange={(e) => setForm({ ...form, duration_sec: parseInt(e.target.value) || 8 })} /></div>
            </div>
            <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div><Label>Visual prompt (used for generation)</Label><Textarea rows={3} value={form.prompt} onChange={(e) => setForm({ ...form, prompt: e.target.value })} data-testid="scene-prompt" /></div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
              <Button onClick={createScene} data-testid="save-scene-btn">Add scene</Button>
            </div>
          </CardContent>
        </Card>
      )}
      {scenes.length === 0 && !creating ? (
        <Card className="border-dashed border-2"><CardContent className="py-10 text-center text-sm text-slate-500">
          No scenes yet. Click <strong>Auto-Storyboard</strong> to have the AI break your brief into a scene-by-scene shot list, or add scenes manually.
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {scenes.sort((a, b) => a.sequence - b.sequence).map((s) => (
            <Card key={s.id} data-testid={`scene-${s.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white font-bold text-lg shrink-0">
                    {s.sequence}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center flex-wrap gap-2 mb-1">
                      <h3 className="font-semibold">{s.title}</h3>
                      {s.shot_type && <Badge className="bg-slate-100 text-slate-700 border-0 text-[10px]">{s.shot_type}</Badge>}
                      <Badge className="bg-emerald-100 text-emerald-800 border-0 text-[10px]">{s.duration_sec}s</Badge>
                      <Badge className={`border-0 text-[10px] ${s.status === 'complete' ? 'bg-emerald-100 text-emerald-800' : s.status === 'generating' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-700'}`}>{s.status}</Badge>
                    </div>
                    <p className="text-xs text-slate-600 mt-1">{s.description}</p>
                    {s.prompt && <p className="text-[11px] text-slate-500 mt-1 italic line-clamp-2">Prompt: {s.prompt}</p>}
                    {s.voice_over_text && <p className="text-[11px] text-slate-500 mt-1">🎙 {s.voice_over_text}</p>}
                    {genState[s.id] && (
                      <div className="mt-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-2.5 dark:bg-emerald-500/10" data-testid={`scene-progress-${s.id}`}>
                        <GenerationProgress
                          kind={genState[s.id].kind}
                          startedAt={genState[s.id].startedAt}
                          variant="compact"
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => genScene(s.id, 'image')} data-testid={`gen-image-${s.id}`}><ImageIcon className="h-3.5 w-3.5 mr-1" />Image</Button>
                    <Button size="sm" variant="outline" onClick={() => genScene(s.id, 'video')} data-testid={`gen-video-${s.id}`}><Play className="h-3.5 w-3.5 mr-1" />Video</Button>
                    <Button size="sm" variant="ghost" onClick={() => removeScene(s.id)} className="text-rose-500 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══ Outputs tab (with Phase 4 consistency scoring) ══════════════════════════
function OutputsTab({ outputs, onReload, project, brand }) {
  const [scoring, setScoring] = useState(null);
  const scoreItem = async (item) => {
    setScoring(item.id);
    try {
      const refs = []; // future: pull brand + char + product refs
      const { data } = await axios.post(`${API}/api/koolkonnect/library/${item.id}/score`, {
        reference_urls: refs,
        expectations: brand ? `brand ${brand.name}, colors ${(brand.colors || []).join(', ')}, voice/tone ${brand.voice_tone || ''}` : 'brand consistency',
      }, auth());
      toast.success(`Scored ${data.score ?? '?'} — ${data.passes ? 'PASSES' : 'flagged'}`);
      onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Scoring failed'); }
    finally { setScoring(null); }
  };
  if (outputs.length === 0) return <Card className="border-dashed border-2"><CardContent className="py-10 text-center text-sm text-slate-500">No outputs yet. Generate from the Scenes tab or from the main KoolKonnect Library.</CardContent></Card>;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
      {outputs.map((o) => (
        <Card key={o.id} className="group relative">
          <div className="aspect-square bg-slate-100 flex items-center justify-center overflow-hidden">
            {(o.category === 'image' || (o.meta?.mime || '').startsWith('image/')) && o.url && (
              <img src={o.url?.startsWith('http') ? o.url : `${API}${o.url}`} alt={o.name} className="h-full w-full object-cover" />
            )}
            {o.category === 'video' && <Film className="h-8 w-8 text-slate-400" />}
          </div>
          <CardContent className="p-2">
            <div className="text-xs font-medium truncate">{o.name}</div>
            {o.consistency ? (
              <div className="mt-1 flex items-center gap-1">
                <Badge className={`border-0 text-[10px] ${o.consistency.passes ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                  <ShieldCheck className="h-3 w-3 mr-1" /> {o.consistency.score ?? '?'}
                </Badge>
                {o.consistency.issues?.length > 0 && (
                  <span className="text-[10px] text-slate-500 truncate">{o.consistency.issues[0]}</span>
                )}
              </div>
            ) : (
              <button
                onClick={() => scoreItem(o)}
                disabled={scoring === o.id}
                className="text-[10px] text-indigo-600 hover:text-indigo-800 mt-1 disabled:text-slate-400"
                data-testid={`score-${o.id}`}
              >
                {scoring === o.id ? 'Scoring…' : 'Run QA scoring'}
              </button>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ═══ Assembly tab ════════════════════════════════════════════════════════════
function AssemblyTab({ pid, scenes, outputs }) {
  const [manifest, setManifest] = useState({ tracks: [], overlays: [], duration_sec: 0, export_settings: { aspect_ratio: '9:16', format: 'mp4', resolution: '1080p' } });
  const [saving, setSaving] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [renderStartedAt, setRenderStartedAt] = useState(null);
  const [lastRender, setLastRender] = useState(null);
  const [renderPricePerMin, setRenderPricePerMin] = useState(null); // v2.9.9 badge
  useEffect(() => {
    axios.get(`${API}/api/koolkonnect/projects/${pid}/assembly`, auth()).then((r) => {
      if (r.data && r.data.id) {
        setManifest(r.data);
        if (r.data.last_render_url) setLastRender({ url: r.data.last_render_url, at: r.data.last_rendered_at });
      }
    });
    // v2.9.9 (Hub v10.7.6) — Fetch sticky retail credits for the MP4 render.
    axios
      .get(`${API}/api/pricing/retail/quote`, { ...auth(), params: { action_code: 'VIDEO.render_mp4' } })
      .then((r) => setRenderPricePerMin(r.data?.retail_credits ?? null))
      .catch(() => setRenderPricePerMin(null));
  }, [pid]);
  // Round duration up to a whole minute since Hub bills per-minute.
  const projectMinutes = Math.max(1, Math.ceil(((manifest?.duration_sec) || 0) / 60));
  const totalRenderCredits = renderPricePerMin != null ? renderPricePerMin * projectMinutes : null;
  const render = async () => {
    setRendering(true);
    setRenderStartedAt(Date.now());
    try {
      const { data } = await axios.post(`${API}/api/koolkonnect/projects/${pid}/assembly/render`, {}, auth());
      if (data.status === 'complete') {
        setLastRender({ url: data.url, at: new Date().toISOString() });
        toast.success('Render complete');
      } else {
        toast.info(data.note || `Render ${data.status}`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Render failed'); }
    finally { setRendering(false); setRenderStartedAt(null); }
  };
  const seedFromScenes = () => {
    let t = 0;
    const clips = scenes.sort((a, b) => a.sequence - b.sequence).flatMap((s) => {
      const items = (s.asset_ids || []).map((aid) => outputs.find((o) => o.id === aid)).filter(Boolean);
      return items.map((asset) => {
        const clip = { asset_id: asset.id, start_sec: t, end_sec: t + (s.duration_sec || 8), scene_id: s.id, label: `Scene ${s.sequence}` };
        t += (s.duration_sec || 8);
        return clip;
      });
    });
    setManifest((m) => ({ ...m, duration_sec: t, tracks: [{ id: 'video', kind: 'video', clips }, { id: 'voice', kind: 'voice', clips: [] }, { id: 'music', kind: 'music', clips: [] }, { id: 'subtitle', kind: 'subtitle', clips: [] }] }));
    toast.success(`Seeded ${clips.length} clips (${t}s)`);
  };
  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/koolkonnect/projects/${pid}/assembly`, manifest, auth());
      toast.success('Assembly saved');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div>
          <p className="text-sm text-slate-600">Assemble the final deliverable — tracks, overlays, subtitles, CTA end cards.</p>
          <p className="text-[11px] text-slate-500">The rendered output will be produced by a dedicated editor engine in a later phase; for now, the assembly manifest is preserved so nothing is lost.</p>
        </div>
        <div className="flex gap-2 items-center">
          <Button variant="outline" onClick={seedFromScenes} data-testid="seed-assembly-btn"><RefreshCw className="h-4 w-4 mr-1" /> Seed from scenes</Button>
          <Button onClick={save} disabled={saving} data-testid="save-assembly-btn">{saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Save assembly</Button>
          {totalRenderCredits != null && (
            <Badge
              variant="secondary"
              className="bg-emerald-50 text-emerald-700 border border-emerald-200"
              data-testid="render-credit-badge"
              title={`Hub render bills per minute: ${renderPricePerMin} cr × ${projectMinutes} min`}
            >
              {totalRenderCredits} cr
            </Badge>
          )}
          <Button onClick={render} disabled={rendering} className="bg-emerald-600 hover:bg-emerald-700" data-testid="render-btn">
            {rendering ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Rendering…</> : <><Play className="h-4 w-4 mr-1" /> Render MP4</>}
          </Button>
        </div>
      </div>

      {rendering && (
        <Card className="border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10" data-testid="render-progress">
          <CardContent className="p-4">
            <GenerationProgress kind="render" startedAt={renderStartedAt} variant="compact" />
            <p className="text-[11px] text-slate-500 mt-2">
              We&apos;re assembling your scenes into a single MP4 on the Kaharian Hub render farm. Longer timelines take up to two minutes — leave this tab open.
            </p>
          </CardContent>
        </Card>
      )}

      {lastRender && (
        <Card>
          <CardContent className="p-3 flex items-center gap-3">
            <Play className="h-4 w-4 text-emerald-600 shrink-0" />
            <div className="text-xs flex-1 min-w-0">
              <div className="font-medium">Latest render</div>
              <a href={`${API}${lastRender.url}`} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline truncate block">{lastRender.url}</a>
            </div>
            <video controls src={`${API}${lastRender.url}`} className="h-16 rounded bg-black" />
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Export settings</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label>Aspect ratio</Label>
            <Select value={manifest.export_settings?.aspect_ratio || '9:16'} onValueChange={(v) => setManifest({ ...manifest, export_settings: { ...manifest.export_settings, aspect_ratio: v } })}>
              <SelectTrigger data-testid="export-aspect"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="9:16">9:16 (Reels / TikTok / Shorts)</SelectItem>
                <SelectItem value="1:1">1:1 (Square feed)</SelectItem>
                <SelectItem value="4:5">4:5 (Instagram portrait)</SelectItem>
                <SelectItem value="16:9">16:9 (YouTube / landscape)</SelectItem>
                <SelectItem value="3:4">3:4 (Pinterest / classic)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Format</Label>
            <Select value={manifest.export_settings?.format || 'mp4'} onValueChange={(v) => setManifest({ ...manifest, export_settings: { ...manifest.export_settings, format: v } })}>
              <SelectTrigger data-testid="export-format"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="mp4">MP4 (H.264)</SelectItem>
                <SelectItem value="mov">MOV (QuickTime)</SelectItem>
                <SelectItem value="webm">WEBM (web-optimized)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Resolution</Label>
            <Select value={manifest.export_settings?.resolution || '1080p'} onValueChange={(v) => setManifest({ ...manifest, export_settings: { ...manifest.export_settings, resolution: v } })}>
              <SelectTrigger data-testid="export-resolution"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="720p">720p (HD)</SelectItem>
                <SelectItem value="1080p">1080p (Full HD)</SelectItem>
                <SelectItem value="1440p">1440p (2K)</SelectItem>
                <SelectItem value="2160p">2160p (4K)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Timeline · {manifest.duration_sec || 0}s total</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {(manifest.tracks || []).length === 0 ? (
            <p className="text-xs text-slate-500 italic">Empty. Click &quot;Seed from scenes&quot; above to auto-populate from your storyboard.</p>
          ) : (
            (manifest.tracks || []).map((tr, ti) => (
              <div key={tr.id || ti} className="border rounded p-2 bg-slate-50">
                <div className="flex items-center gap-2 mb-2">
                  {tr.kind === 'video' ? <Film className="h-3.5 w-3.5" /> : tr.kind === 'voice' ? <ScrollText className="h-3.5 w-3.5" /> : tr.kind === 'music' ? <Music className="h-3.5 w-3.5" /> : <ScrollText className="h-3.5 w-3.5" />}
                  <span className="text-xs font-medium capitalize">{tr.kind}</span>
                  <Badge className="bg-slate-200 border-0 text-[10px]">{(tr.clips || []).length} clips</Badge>
                </div>
                <div className="flex gap-1 overflow-x-auto">
                  {(tr.clips || []).map((c, ci) => (
                    <div key={ci} className="shrink-0 min-w-[80px] rounded bg-white border p-1.5 text-[10px]">
                      <div className="font-medium truncate">{c.label || `Clip ${ci + 1}`}</div>
                      <div className="text-slate-500">{c.start_sec}s–{c.end_sec}s</div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ═══ Backup tab (Phase 8) ═══════════════════════════════════════════════════
function BackupTab() {
  const [cfg, setCfg] = useState(null);
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    const [{ data: c }] = await Promise.all([
      axios.get(`${API}/api/koolkonnect/backup/status`, auth()),
    ]);
    setCfg(c);
  };
  useEffect(() => { load(); }, []);
  const save = async () => {
    await axios.put(`${API}/api/koolkonnect/backup/settings`, cfg, auth());
    toast.success('Backup settings saved');
  };
  const runNow = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/koolkonnect/backup/run`, {}, auth());
      toast.success(`Backup ${data.status} · ${data.asset_count} assets`);
      if (data.note) toast.info(data.note);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Backup failed'); }
    finally { setBusy(false); }
  };
  if (!cfg) return <Loader2 className="h-4 w-4 animate-spin" />;
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><CloudUpload className="h-4 w-4" /> Google Drive Backup</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className={`rounded p-3 text-sm ${cfg.google_drive_connected ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
            {cfg.google_drive_connected
              ? '✓ Google Drive connected — backups will run against your account.'
              : 'Google Drive is not connected yet. Backups will be queued but not uploaded until you connect your Drive account in a future update.'}
          </div>
          <div className="flex items-center justify-between gap-3">
            <div><Label>Auto backup before 30-day retention expiry</Label><p className="text-[11px] text-slate-500 mt-0.5">We&apos;ll automatically upload eligible assets 7 days before they&apos;re due to be deleted.</p></div>
            <Switch checked={!!cfg.auto_backup_enabled} onCheckedChange={(v) => setCfg({ ...cfg, auto_backup_enabled: v })} data-testid="auto-backup-toggle" />
          </div>
          <div>
            <Label>Categories to back up</Label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {['final', 'video', 'image', 'voice', 'music', 'brand', 'product_ref', 'character_ref'].map((c) => (
                <button
                  key={c}
                  onClick={() => setCfg({ ...cfg, backup_categories: (cfg.backup_categories || []).includes(c) ? cfg.backup_categories.filter((x) => x !== c) : [...(cfg.backup_categories || []), c] })}
                  className={`px-3 py-1 text-xs rounded-full border ${(cfg.backup_categories || []).includes(c) ? 'bg-indigo-500 text-white border-indigo-500' : 'bg-white border-slate-200'}`}
                >{c}</button>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={runNow} disabled={busy} data-testid="run-backup-btn">
              {busy ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Running…</> : <><CloudUpload className="h-4 w-4 mr-1" /> Backup now</>}
            </Button>
            <Button onClick={save} data-testid="save-backup-btn"><Save className="h-4 w-4 mr-1" /> Save settings</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
